import type { PropsWithChildren } from "react";
import { Navigate, Outlet, Routes } from "react-router-dom";

import { Layout } from "@/app/components/layouts";
import type { PortalUser } from "@/app/models";
import { ROUTES, useSetPortalUser } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { LogoLoader } from "@/shared/components";
import { useLanguageFromParams } from "@/shared/hooks";
import { useAuthStore } from "@/shared/stores";

const RequireLoggedIn = ({ children }: PropsWithChildren) => {
  useLanguageFromParams();

  const isLoggedIn = useAuthStore((state) => Boolean(state.token));

  if (!isLoggedIn) {
    return <Navigate replace to={ROUTES.LOGIN} />;
  }

  return children;
};

type RequireUserTypesProps = PropsWithChildren & {
  allowedUserTypes: PortalUser["type"][];
};

export const RequireUserType = ({
  children,
  allowedUserTypes,
}: RequireUserTypesProps) => {
  const userType = usePortalUserStore((state) => state.portalUser?.type);

  if (allowedUserTypes && userType && !allowedUserTypes.includes(userType)) {
    return <Navigate replace to={ROUTES.BASE} />;
  }

  return (
    <>
      {children}
      <Outlet />
    </>
  );
};

export const PortalRoutesWrapper = ({ children }: PropsWithChildren) => {
  const { isLoading } = useSetPortalUser();
  const hasPortalUser = usePortalUserStore((state) =>
    Boolean(state.portalUser),
  );

  if (isLoading) {
    return <LogoLoader fullscreen variant="secondary" />;
  }

  return (
    <RequireLoggedIn>
      {hasPortalUser && (
        <Layout>
          {children && <Routes>{children}</Routes>}
          <Outlet />
        </Layout>
      )}
    </RequireLoggedIn>
  );
};
